import { useMemo } from "react";

import { NO_ORG_ID, useFastshiftSelector } from "../../../state";

import { useCustomerSupportRequests } from "./use-customer-support-requests";

// add ticket.destinationLicense as a fallback field for ticket.atlassianCloudUrl later
export const useTicketsFilteredBySelectedOrg = () => {
    const {
        data: customerSupportRequests,
        pending: isCustomerSupportRequestsPending,
        error: hasCustomerSupportRequestsErrors,
        retry: retryCustomerSupportRequests
    } = useCustomerSupportRequests();

    const cloudSitesByOrg = useFastshiftSelector(
        (state) => state.cloudSitesByOrg
    );

    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);

    // all cloud sites connected to an org the user is an admin of
    const allCloudUrls = useMemo(() => {
        return cloudSitesByOrg.data.length > 0
            ? cloudSitesByOrg.data
                  .flatMap((org) => org.cloudSites)
                  .map((site) => site.baseUrl)
            : [];
    }, [cloudSitesByOrg.data]);

    const filteredTickets = useMemo(() => {
        if (!selectedOrg || cloudSitesByOrg.pending) {
            return [];
        }

        // 'no org' = tickets that the user does not have org admin permissions in relation to
        if (selectedOrg.id === NO_ORG_ID) {
            return customerSupportRequests.filter(
                (ticket) =>
                    !ticket?.atlassianCloudUrl ||
                    !allCloudUrls.includes(ticket.atlassianCloudUrl)
            );
        }

        // tickets related to the selected org
        const orgCloudSites = cloudSitesByOrg.data.find(
            (org) => org.orgId === selectedOrg.id
        )?.cloudSites;

        return customerSupportRequests.filter((ticket) => {
            return orgCloudSites?.some((site) =>
                [ticket.atlassianCloudUrl, ticket.destinationLicense].includes(
                    site.baseUrl
                )
            );
        });
    }, [
        customerSupportRequests,
        cloudSitesByOrg.data,
        selectedOrg,
        allCloudUrls,
        cloudSitesByOrg.pending
    ]);

    return {
        filteredTickets,
        isPending: cloudSitesByOrg.pending || isCustomerSupportRequestsPending,
        hasError: !!hasCustomerSupportRequestsErrors,
        retry: retryCustomerSupportRequests
    };
};
